import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, CheckCircle, ExternalLink } from 'lucide-react';
import axios from 'axios';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { trackEvent } from '../lib/analytics';
import './phone-input.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const WHATSAPP_NUMBER = '212661907443';

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const CTA = () => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    website: '', // honeypot (must stay empty)
  });
  const [phone, setPhone] = useState('');
  const [defaultCountry, setDefaultCountry] = useState('MA');
  const [token, setToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const sectionRef = useRef(null);
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);
  const isRTL = i18n.language === 'ar';

  // Detect the visitor's country so the phone field defaults to their dial code.
  useEffect(() => {
    let active = true;
    fetch('https://ipapi.co/json/')
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (active && data && data.country_code) {
          setDefaultCountry(data.country_code);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Load & render the Cloudflare Turnstile widget.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let cancelled = false;

    const render = () => {
      if (cancelled || !window.turnstile || !turnstileRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action: 'contact',
        theme: 'dark',
        language: i18n.language,
        callback: (tok) => {
          setToken(tok);
          setError(null);
        },
        'expired-callback': () => setToken(''),
        'error-callback': () => {
          setToken('');
          return true;
        },
      });
    };

    if (window.turnstile) {
      render();
    } else if (!document.querySelector(`script[src="${TURNSTILE_SRC}"]`)) {
      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.defer = true;
      script.onload = render;
      document.head.appendChild(script);
    } else {
      const iv = setInterval(() => {
        if (window.turnstile) {
          clearInterval(iv);
          render();
        }
      }, 200);
      setTimeout(() => clearInterval(iv), 6000);
    }

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          /* noop */
        }
      }
      widgetIdRef.current = null;
    };
  }, [i18n.language]);

  const resetTurnstile = () => {
    setToken('');
    if (window.turnstile && widgetIdRef.current !== null) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (e) {
        /* noop */
      }
    }
  };

  const buildWhatsAppUrl = () => {
    const text = t('cta.form.whatsappTemplate', {
      name: formData.name || '-',
      company: formData.company || '-',
      phone: phone || '-',
      message: formData.message || '-',
    });
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
  };

  const handleWhatsApp = () => {
    trackEvent('generate_lead', {
      method: 'whatsapp',
      language: i18n.language,
      has_company: Boolean(formData.company),
    });
    window.open(buildWhatsAppUrl(), '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Require the Turnstile challenge to be solved.
    if (TURNSTILE_SITE_KEY && !token) {
      setError(t('cta.form.verifyRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/contact`, { ...formData, phone, turnstileToken: token });
      
      setIsSubmitting(false);
      setIsSubmitted(true);

      // GA4 conversion event (recommended event for Google Ads lead-gen import)
      trackEvent('generate_lead', {
        method: 'contact_form',
        language: i18n.language,
        has_company: Boolean(formData.company),
      });

      setFormData({ name: '', email: '', company: '', message: '', website: '' });
      setPhone('');
      resetTurnstile();
      
      // Redirect to booking page after 2 seconds
      setTimeout(() => {
        trackEvent('booking_redirect', { destination: 'strategy_call' });
        window.location.href = response.data.redirect_url || 'https://meet.crescents.ma/free-strategy-call';
      }, 2000);
      
    } catch (err) {
      setIsSubmitting(false);
      setError(t('cta.form.error'));
      resetTurnstile();
    }
  };

  const benefits = t('cta.benefits', { returnObjects: true });

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-black"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Elements */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} w-[400px] h-[400px] bg-[#00FFD1]/5 rounded-full blur-[150px] pointer-events-none`} />
      <div className={`absolute bottom-0 ${isRTL ? 'right-0' : 'left-0'} w-[300px] h-[300px] bg-[#00FFD1]/3 rounded-full blur-[120px] pointer-events-none`} />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        <div className={`grid lg:grid-cols-2 gap-16 lg:gap-24 items-start ${isRTL ? 'lg:grid-flow-dense' : ''}`}>
          {/* Left Column - Content */}
          <div className={isRTL ? 'lg:col-start-2' : ''}>
            {/* Section Label */}
            <div
              className={`inline-flex items-center gap-3 mb-6 opacity-0 ${
                isVisible ? 'animate-slide-in-left' : ''
              }`}
            >
              <span className="w-12 h-[2px] bg-[#00FFD1]" />
              <span className="text-[#00FFD1] text-sm font-medium tracking-wider uppercase">
                {t('cta.badge')}
              </span>
            </div>

            {/* Title */}
            <h2
              className={`display-large text-white mb-6 opacity-0 ${
                isVisible ? 'animate-slide-in-left delay-100' : ''
              }`}
            >
              {t('cta.title')}
            </h2>

            {/* Subtitle */}
            <p
              className={`body-medium text-white/70 mb-10 max-w-lg opacity-0 ${
                isVisible ? 'animate-slide-in-left delay-200' : ''
              }`}
            >
              {t('cta.subtitle')}
            </p>

            {/* Benefits List */}
            <div
              className={`space-y-4 opacity-0 ${
                isVisible ? 'animate-slide-in-left delay-300' : ''
              }`}
            >
              {benefits.map((benefit, index) => (
                <div key={benefit} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-[#00FFD1]/10">
                    <CheckCircle className="w-4 h-4 text-[#00FFD1]" />
                  </div>
                  <span className="text-white/70 text-sm">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Contact Info */}
            <div
              className={`mt-12 pt-8 border-t border-white/10 opacity-0 ${
                isVisible ? 'animate-slide-in-left delay-400' : ''
              }`}
            >
              <p className="text-white/40 text-sm mb-2">{t('cta.preferEmail')}</p>
              <a
                href="mailto:oualid@bennaid.com"
                className="text-[#00FFD1] hover:text-white transition-colors duration-300"
              >
                oualid@bennaid.com
              </a>
            </div>
          </div>

          {/* Right Column - Form */}
          <div
            className={`relative p-8 md:p-10 bg-white/[0.02] border border-white/10 opacity-0 ${
              isVisible ? 'animate-slide-in-right delay-200' : ''
            } ${isRTL ? 'lg:col-start-1' : ''}`}
          >
            {/* Corner Accents */}
            <div className={`absolute top-0 ${isRTL ? 'right-0 border-t-2 border-r-2' : 'left-0 border-t-2 border-l-2'} w-8 h-8 border-[#00FFD1]`} />
            <div className={`absolute bottom-0 ${isRTL ? 'left-0 border-b-2 border-l-2' : 'right-0 border-b-2 border-r-2'} w-8 h-8 border-[#00FFD1]`} />

            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-[#00FFD1]/10 mb-6">
                  <CheckCircle className="w-8 h-8 text-[#00FFD1]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {t('cta.form.success')}
                </h3>
                <p className="text-white/60 text-sm mb-4">
                  {t('cta.form.redirecting')}
                </p>
                <div className="flex items-center gap-2 text-[#00FFD1]">
                  <ExternalLink className="w-4 h-4 animate-pulse" />
                  <span className="text-sm">{t('cta.form.opening')}</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                {/* Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white/70 text-sm">
                    {t('cta.form.name')}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder={t('cta.form.namePlaceholder')}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00FFD1] transition-colors"
                  />
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/70 text-sm">
                    {t('cta.form.email')}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={t('cta.form.emailPlaceholder')}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    maxLength={254}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00FFD1] transition-colors"
                  />
                </div>

                {/* Phone Field with country-code selector (auto-detects visitor country) */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white/70 text-sm">
                    {t('cta.form.phone')}
                  </Label>
                  <PhoneInput
                    id="phone"
                    name="phone"
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry={defaultCountry}
                    value={phone}
                    onChange={(value) => {
                      setPhone(value || '');
                      setError(null);
                    }}
                    placeholder={t('cta.form.phonePlaceholder')}
                    className="obn-phone"
                    data-testid="contact-phone-input"
                  />
                </div>

                {/* Honeypot field - hidden from real users; bots that fill it are dropped */}
                <div aria-hidden="true" className="hidden">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleChange}
                  />
                </div>

                {/* Company Field */}
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-white/70 text-sm">
                    {t('cta.form.company')}
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    placeholder={t('cta.form.companyPlaceholder')}
                    value={formData.company}
                    onChange={handleChange}
                    maxLength={150}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00FFD1] transition-colors"
                  />
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-white/70 text-sm">
                    {t('cta.form.message')}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder={t('cta.form.messagePlaceholder')}
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    required
                    maxLength={2000}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00FFD1] transition-colors resize-none"
                  />
                </div>

                {/* Cloudflare Turnstile bot-protection widget */}
                <div ref={turnstileRef} data-testid="turnstile-widget" className="min-h-[65px]" />

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary group"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      {t('cta.form.sending')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {t('cta.form.submit')}
                      <Send className={`w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 ${isRTL ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
                    </span>
                  )}
                </button>

                {/* WhatsApp alternative */}
                <button
                  type="button"
                  onClick={handleWhatsApp}
                  data-testid="contact-whatsapp-button"
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 transition-colors duration-300 text-sm font-medium"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                  {t('cta.form.whatsapp')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
